import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SiteSearch from "@/components/feature/SiteSearch";

interface FeaturedLink { label: string; href: string; icon: string; desc: string; highlight?: boolean; }
interface ChildLink { label: string; href: string; icon: string; desc: string; }
interface NavLink { label: string; href: string; children?: ChildLink[]; featured?: FeaturedLink[]; }

const navLinks: NavLink[] = [
  {
    label: "Solutions",
    href: "/solutions",
    children: [
      { label: "Solutions Overview", href: "/solutions", icon: "ri-layout-grid-line", desc: "All solutions at a glance" },
      { label: "Surveillance & Security", href: "/solutions/surveillance", icon: "ri-shield-user-line", desc: "Body-worn cameras & monitoring" },
      { label: "Rugged Computing", href: "/solutions/rugged-computing", icon: "ri-computer-line", desc: "MIL-SPEC field devices" },
      { label: "HCI & Storage Infrastructure", href: "/solutions/data-protection", icon: "ri-stack-line", desc: "VMware alternative, backup & NAS" },
    ],
    featured: [
      { label: "Free Migration Assessment", href: "/arcfra-assessment", icon: "ri-calendar-check-line", desc: "Book a free VMware/Nutanix assessment", highlight: true },
      { label: "Hytera BWC Spotlight", href: "/hytera-bwv", icon: "ri-camera-3-line", desc: "Body-worn camera solutions for law enforcement" },
      { label: "Arcfra Platform Spotlight", href: "/arcfra-spotlight", icon: "ri-server-line", desc: "Full-stack AECP — AVE, ABS, AKE, ANS, AFS" },
      { label: "ExaGrid Backup Spotlight", href: "/exagrid-spotlight", icon: "ri-shield-keyhole-line", desc: "Full appliance lineup — EX10 to EX84" },
      { label: "ExaGrid Backup Malaysia", href: "/exagrid-backup-malaysia", icon: "ri-price-tag-3-line", desc: "Sizing, pricing & free assessment", highlight: false },
      { label: "Rugged Computing Spotlight", href: "/rugged-spotlight", icon: "ri-cpu-line", desc: "MIL-SPEC rugged devices for field teams" },
      { label: "Body-Worn Camera Malaysia", href: "/body-worn-camera-malaysia", icon: "ri-camera-line", desc: "SC880, SC780, GC550 — demo & pricing" },
      { label: "Rugged Tablet Malaysia", href: "/rugged-tablet-malaysia", icon: "ri-tablet-line", desc: "Honeywell & Dell Rugged — quote & demo" },
      { label: "VMware Alternative Malaysia", href: "/vmware-alternative-malaysia", icon: "ri-exchange-line", desc: "Arcfra HCI — migrate off VMware, quote & demo" },
    ],
  },
  { label: "Home", href: "/" },
  { label: "Industries", href: "/industries" },
  { label: "Technology Partners", href: "/brands" },
  { label: "Case Studies", href: "/case-studies" },
  { label: "About", href: "/about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [expertOpen, setExpertOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleDropdownEnter = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setSolutionsOpen(true);
  };

  const handleDropdownLeave = () => {
    closeTimerRef.current = setTimeout(() => setSolutionsOpen(false), 200);
  };

  const goHome = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate("/");
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSolutionsOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleSearchNavigate = (query: string) => {
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setSearchOpen(false);
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white shadow-sm" : "bg-black/30 backdrop-blur-sm"
        }`}
      >
        <nav className="w-full px-8 lg:px-16 grid grid-cols-[1fr_auto_1fr] items-center h-16 lg:h-20">
          {/* Logo — pinned left */}
          <a
            href="/"
            className="flex items-center gap-3 flex-shrink-0 cursor-pointer justify-self-start"
            onClick={goHome}
          >
            <img
              src="https://storage.readdy-site.link/project_files/15616acb-dd20-42a3-bb13-24e14d98a32e/707ceb8d-a118-467e-a1f1-a92ec3214991_SED-Logo.png?v=25b700dde891a260d3a0501eba34a289"
              alt="SE Distribution"
              className="h-10 w-auto"
            />
            <div className={`hidden lg:flex flex-col leading-tight border-l pl-3 transition-colors ${scrolled ? "border-gray-200" : "border-white/20"}`}>
              <span className={`text-xs font-bold tracking-wide whitespace-nowrap ${scrolled ? "text-[#1a1a1a]" : "text-white"}`}>SE Distribution Sdn Bhd</span>
              <span className={`text-[10px] whitespace-nowrap ${scrolled ? "text-gray-400" : "text-white/50"}`}>Reg: 202001036939 (1393260-U)</span>
            </div>
          </a>

          {/* Desktop Nav — perfectly centered */}
          <div className="hidden md:flex items-center justify-center gap-6 lg:gap-8">
            {navLinks.map((link) =>
              link.children ? (
                <div key={link.label} className="relative group">
                  {(() => {
                    const isSolutionsActive =
                      location.pathname.startsWith("/solutions") ||
                      location.pathname.startsWith("/arcfra") ||
                      location.pathname.startsWith("/exagrid") ||
                      location.pathname.startsWith("/hytera") ||
                      location.pathname.startsWith("/rugged") ||
                      location.pathname.startsWith("/vmware") ||
                      location.pathname.startsWith("/nas");
                    return (
                      <button
                        className="relative flex flex-col items-start cursor-pointer whitespace-nowrap pb-0.5 group/solbtn"
                        onMouseEnter={handleDropdownEnter}
                        onMouseLeave={handleDropdownLeave}
                      >
                        <span className={`text-sm font-medium flex items-center gap-1 transition-colors ${
                          solutionsOpen
                            ? scrolled ? "text-[#1a1a1a]" : "text-white"
                            : isSolutionsActive
                              ? scrolled ? "text-[#1a1a1a]" : "text-white"
                              : scrolled ? "text-[#1a1a1a] hover:text-[#555]" : "text-white/90 hover:text-white"
                        }`}>
                          {link.label}
                          <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${solutionsOpen ? "rotate-180" : ""}`}></i>
                        </span>
                        <span
                          className={`text-[9px] font-medium tracking-wide leading-none transition-all duration-300 overflow-hidden ${
                            solutionsOpen
                              ? "max-h-4 opacity-100 mt-0.5 " + (scrolled ? "text-[#1a1a1a]/40" : "text-white/50")
                              : "max-h-0 opacity-0"
                          }`}
                        >
                          Explore our solution areas
                        </span>
                        {/* Active underline */}
                        {isSolutionsActive && (
                          <span className={`absolute bottom-0 left-0 right-0 h-[2px] rounded-full ${scrolled ? "bg-[#1a1a1a]" : "bg-white"}`} />
                        )}
                      </button>
                    );
                  })()}
                  {solutionsOpen && (
                    <div
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-2xl border border-gray-100 overflow-hidden"
                      style={{ width: "860px", boxShadow: "0 8px 40px rgba(0,0,0,0.10)" }}
                      onMouseEnter={handleDropdownEnter}
                      onMouseLeave={handleDropdownLeave}
                    >
                      {link.featured && (() => {
                        const highlightItem = link.featured.find(f => f.highlight);
                        const spotlights = link.featured.filter(f => !f.highlight && ["/hytera-bwv", "/arcfra-spotlight", "/exagrid-spotlight", "/rugged-spotlight"].includes(f.href));
                        const landingPages = link.featured.filter(f => !f.highlight && !["/hytera-bwv", "/arcfra-spotlight", "/exagrid-spotlight", "/rugged-spotlight"].includes(f.href));
                        return (
                          <div className="grid grid-cols-3 divide-x divide-gray-100">

                            {/* COL 1 — Solution Categories */}
                            <div className="p-5">
                              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-3">Solution Areas</p>
                              <div className="flex flex-col gap-1">
                                {link.children.map((child) => (
                                  <Link
                                    key={child.href}
                                    to={child.href}
                                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#f5f3ee] transition-colors group cursor-pointer"
                                  >
                                    <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a]/6 group-hover:bg-[#1a1a1a] rounded-lg flex-shrink-0 transition-colors">
                                      <i className={`${child.icon} text-[#1a1a1a] group-hover:text-white text-sm transition-colors`}></i>
                                    </div>
                                    <div className="min-w-0">
                                      <p className="text-sm font-semibold text-[#1a1a1a] leading-tight">{child.label}</p>
                                      <p className="text-[10px] text-gray-400 mt-0.5 leading-tight truncate">{child.desc}</p>
                                    </div>
                                    <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-[#1a1a1a] ml-auto flex-shrink-0 transition-colors"></i>
                                  </Link>
                                ))}
                              </div>
                              {/* Free Assessment CTA at bottom of col 1 */}
                              {highlightItem && (
                                <Link
                                  to={highlightItem.href}
                                  className="mt-4 flex items-center gap-3 px-3 py-3 rounded-xl bg-[#1a1a1a] hover:bg-[#333] transition-colors group cursor-pointer"
                                >
                                  <div className="w-8 h-8 flex items-center justify-center bg-white/15 rounded-lg flex-shrink-0">
                                    <i className={`${highlightItem.icon} text-white text-sm`}></i>
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-xs font-bold text-white leading-tight">Free Migration Assessment</p>
                                    <p className="text-[10px] text-white/55 mt-0.5 leading-tight truncate">{highlightItem.desc}</p>
                                  </div>
                                  <i className="ri-arrow-right-line text-white/40 group-hover:text-white ml-auto flex-shrink-0 transition-colors text-xs"></i>
                                </Link>
                              )}
                            </div>

                            {/* COL 2 — Spotlights */}
                            <div className="p-5">
                              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-3">Product Spotlights</p>
                              <div className="flex flex-col gap-1">
                                {spotlights.map(feat => (
                                  <Link
                                    key={feat.href}
                                    to={feat.href}
                                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#f5f3ee] transition-colors group cursor-pointer"
                                  >
                                    <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a] rounded-lg flex-shrink-0">
                                      <i className={`${feat.icon} text-white text-sm`}></i>
                                    </div>
                                    <div className="min-w-0">
                                      <p className="text-xs font-semibold text-[#1a1a1a] leading-tight">{feat.label}</p>
                                      <p className="text-[10px] text-gray-400 mt-0.5 leading-tight truncate">{feat.desc}</p>
                                    </div>
                                    <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-[#1a1a1a] ml-auto flex-shrink-0 transition-colors"></i>
                                  </Link>
                                ))}
                              </div>
                            </div>

                            {/* COL 3 — Pricing & Demos */}
                            <div className="p-5 bg-[#fafaf9]">
                              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-3">Pricing &amp; Demos</p>
                              <div className="flex flex-col gap-1">
                                {landingPages.map(feat => (
                                  <Link
                                    key={feat.href}
                                    to={feat.href}
                                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#eeebe5] transition-colors group cursor-pointer"
                                  >
                                    <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a] rounded-lg flex-shrink-0">
                                      <i className={`${feat.icon} text-white text-sm`}></i>
                                    </div>
                                    <div className="min-w-0">
                                      <p className="text-xs font-semibold text-[#1a1a1a] leading-tight">{feat.label}</p>
                                      <p className="text-[10px] text-gray-400 mt-0.5 leading-tight truncate">{feat.desc}</p>
                                    </div>
                                    <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-[#1a1a1a] ml-auto flex-shrink-0 transition-colors"></i>
                                  </Link>
                                ))}
                              </div>
                            </div>

                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ) : link.label === "Home" ? (
                <a
                  key={link.href}
                  href="/"
                  onClick={goHome}
                  className={`relative text-sm font-medium transition-colors whitespace-nowrap cursor-pointer pb-0.5 ${
                    location.pathname === "/"
                      ? scrolled
                        ? "text-[#1a1a1a] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-[#1a1a1a] after:rounded-full"
                        : "text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-white after:rounded-full"
                      : scrolled ? "text-[#1a1a1a] hover:text-[#555]" : "text-white/90 hover:text-white"
                  }`}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`relative text-sm font-medium transition-colors whitespace-nowrap pb-0.5 ${
                    location.pathname === link.href || location.pathname.startsWith(link.href + "/")
                      ? scrolled
                        ? "text-[#1a1a1a] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-[#1a1a1a] after:rounded-full"
                        : "text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-white after:rounded-full"
                      : scrolled ? "text-[#1a1a1a] hover:text-[#555]" : "text-white/90 hover:text-white"
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
          </div>

          {/* CTA + Search — pinned right */}
          <div className="hidden md:flex items-center justify-end gap-3 flex-shrink-0">
            <button
              onClick={() => setSearchOpen(true)}
              className={`flex items-center gap-2 text-sm px-3.5 py-2 rounded-md border transition-colors cursor-pointer whitespace-nowrap ${
                scrolled
                  ? "border-gray-200 text-gray-500 hover:border-gray-300 hover:text-[#1a1a1a] bg-white"
                  : "border-white/20 text-white/70 hover:text-white hover:border-white/40 bg-white/10"
              }`}
            >
              <i className="ri-search-line text-sm"></i>
              <span className="text-xs">Search</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${scrolled ? "bg-gray-100 text-gray-400" : "bg-white/10 text-white/50"}`}>⌘K</span>
            </button>
            <button
              onClick={() => setExpertOpen(true)}
              className="bg-[#1a1a1a] text-white text-sm font-medium px-5 py-2.5 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
            >
              Contact Us Now
            </button>
          </div>

          {/* Mobile: Search + Hamburger (spans all 3 cols on mobile via col-span) */}
          <div className="md:hidden flex items-center justify-end gap-2 col-start-3">
            <button
              onClick={() => setSearchOpen(true)}
              className={`text-xl cursor-pointer ${scrolled ? "text-[#1a1a1a]" : "text-white"}`}
            >
              <i className="ri-search-line"></i>
            </button>
            <button
              className={`text-xl cursor-pointer ${scrolled ? "text-[#1a1a1a]" : "text-white"}`}
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <i className={mobileOpen ? "ri-close-line" : "ri-menu-line"}></i>
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-3">
            {navLinks.map((link) =>
              link.children ? (
                <div key={link.label}>
                  <p className="text-sm font-semibold text-[#1a1a1a] mb-1">{link.label}</p>
                  {link.children.map((child) => (
                    <Link
                      key={child.href}
                      to={child.href}
                      className="flex items-center gap-2 pl-3 py-1.5 text-sm text-gray-600 hover:text-[#1a1a1a]"
                    >
                      <div className="w-5 h-5 flex items-center justify-center bg-[#1a1a1a]/5 rounded flex-shrink-0">
                        <i className={`${child.icon} text-[#1a1a1a] text-[10px]`}></i>
                      </div>
                      {child.label}
                    </Link>
                  ))}
                  {link.featured && (
                    <div className="mt-2 pl-3 flex flex-col gap-1.5">
                      {link.featured.map((feat) => (
                        feat.highlight ? (
                          <Link
                            key={feat.href}
                            to={feat.href}
                            className="flex items-center gap-2 py-2 px-3 rounded-lg bg-[#1a1a1a] text-white text-sm font-bold"
                          >
                            <div className="w-5 h-5 flex items-center justify-center bg-white/15 rounded flex-shrink-0">
                              <i className={`${feat.icon} text-white text-[10px]`}></i>
                            </div>
                            Free Migration Assessment
                            <i className="ri-arrow-right-line text-white/60 ml-auto text-xs"></i>
                          </Link>
                        ) : (
                          <Link
                            key={feat.href}
                            to={feat.href}
                            className="flex items-center gap-2 py-1.5 text-sm font-medium text-[#1a1a1a]"
                          >
                            <div className="w-5 h-5 flex items-center justify-center bg-[#1a1a1a] rounded flex-shrink-0">
                              <i className={`${feat.icon} text-white text-[10px]`}></i>
                            </div>
                            {feat.label}
                            <span className="text-[10px] text-gray-400 font-normal">— {feat.desc}</span>
                          </Link>
                        )
                      ))}
                    </div>
                  )}
                </div>
              ) : link.label === "Home" ? (
                <a
                  key={link.href}
                  href="/"
                  onClick={(e) => { goHome(e); setMobileOpen(false); }}
                  className="text-sm font-medium text-[#1a1a1a] cursor-pointer"
                >
                  {link.label}
                </a>
              ) : (
                <Link key={link.href} to={link.href} className="text-sm font-medium text-[#1a1a1a]">
                  {link.label}
                </Link>
              )
            )}
            <button
              onClick={() => { setMobileOpen(false); setExpertOpen(true); }}
              className="mt-2 bg-[#1a1a1a] text-white text-sm font-medium px-5 py-2.5 rounded-md text-center whitespace-nowrap cursor-pointer"
            >
              Contact Us Now
            </button>
          </div>
        )}
      </header>

      {/* Site Search Overlay */}
      {searchOpen && (
        <>
          <div className="fixed inset-0 z-[199] bg-black/40 backdrop-blur-sm" />
          <SiteSearch onClose={() => setSearchOpen(false)} onNavigateToSearch={handleSearchNavigate} />
        </>
      )}

      {/* Connect with an Expert Popup */}
      {expertOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setExpertOpen(false)}
        >
          <div
            className="bg-white rounded-xl p-8 max-w-sm w-full mx-4 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setExpertOpen(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-[#1a1a1a] transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>

            <div className="text-center mb-6">
              <div className="w-14 h-14 flex items-center justify-center bg-[#1a1a1a]/5 rounded-full mx-auto mb-4">
                <i className="ri-customer-service-2-line text-[#1a1a1a] text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1a1a1a] mb-1">Contact Us Now</h3>
              <p className="text-sm text-gray-500 leading-relaxed">
                Our solutions team is ready to help you find the right technology for your needs.
              </p>
            </div>

            <div className="space-y-3">
              <a
                href="tel:+60364127917"
                className="flex items-center gap-4 p-4 border border-gray-100 rounded-lg hover:border-[#1a1a1a]/30 hover:bg-[#fafafa] transition-all cursor-pointer group"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a] rounded-lg flex-shrink-0">
                  <i className="ri-phone-line text-white text-base"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Call Us</p>
                  <p className="text-sm font-semibold text-[#1a1a1a] group-hover:underline">+603 6412 7917</p>
                  <p className="text-xs text-gray-400">Mon–Fri, 9:30 AM – 6:30 PM (GMT+8)</p>
                </div>
                <i className="ri-arrow-right-line text-gray-300 group-hover:text-[#1a1a1a] ml-auto transition-colors"></i>
              </a>

              <a
                href="mailto:sales@sedistribution.com.my"
                className="flex items-center gap-4 p-4 border border-gray-100 rounded-lg hover:border-[#1a1a1a]/30 hover:bg-[#fafafa] transition-all cursor-pointer group"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a] rounded-lg flex-shrink-0">
                  <i className="ri-mail-line text-white text-base"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Email Us</p>
                  <p className="text-sm font-semibold text-[#1a1a1a] group-hover:underline">sales@sedistribution.com.my</p>
                  <p className="text-xs text-gray-400">We respond within 1 business day</p>
                </div>
                <i className="ri-arrow-right-line text-gray-300 group-hover:text-[#1a1a1a] ml-auto transition-colors"></i>
              </a>
            </div>

            <div className="mt-5 pt-4 border-t border-gray-100 text-center">
              <Link
                to="/contact"
                onClick={() => setExpertOpen(false)}
                className="text-sm text-gray-500 hover:text-[#1a1a1a] transition-colors"
              >
                Or fill in our enquiry form &rarr;
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
